import RichText from '@/features/shared/RichText';
import { Theme } from '@/utils/constants';
import clsx from 'clsx';
import React, { memo, PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { Fade } from 'react-reveal';
import { AnimatedHeading } from './AnimatedHeading';
import { HeadingsProps } from './Headings.interface';
import useStyles from './Headings.styles';

const Headings = ({
  theme = Theme.light,
  segments: headings = [],
  staticHeadline,
  subheading: description,
  index = 0,
  children,
  animateFromTop,
}: PropsWithChildren<HeadingsProps>) => {
  const classes = useStyles({});
  const subheadingRef = useRef<HTMLDivElement>(null);
  const revealLast = useMemo(() => index === headings.length - 1, [headings.length, index]);

  useEffect(() => {
    if (subheadingRef?.current) {
      const nextHeight = revealLast ? subheadingRef.current?.children[0].clientHeight : 30;
      subheadingRef.current.style.height = `${nextHeight}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const rootClassName = clsx(classes.root, `${theme}-theme`);

  return (
    <div className={rootClassName}>
      <div className={classes.content}>
        <div className={classes.headings}>
          <div className={clsx(classes.heading)}>
            {children}
            <AnimatedHeading
              staticText={staticHeadline}
              list={headings}
              index={index}
              theme={theme}
              staticSegmentClassName={classes.staticSegment}
              animatedSegmentClassName={classes.animatedSegment}
              animateFromTop={animateFromTop}
            />
          </div>
          <div ref={subheadingRef} className={classes.subheading}>
            <Fade up when={revealLast} duration={500} delay={500}>
              <RichText theme={theme} content={description} customStyles />
            </Fade>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Headings);
